import * as React from "react"
import { connect } from "react-redux"

import { toBase64, isImage } from "../../../util/File"
import { GlobalState } from "../../../store"

import { mapDispatchToProps, DispatchProps } from "../actions"

import InlineAttachment from "./InlineAttachment"
import { IAttachment, ILightbox, ISelectedDocument } from "../index"

interface OwnProps {
    ipfValue: File[]
    ipfName: string
    customFieldDefinitionId: string
}

interface StateProps {
    fullName: string
    lightboxIndex: number
    lightbox: ILightbox
}

type Props = OwnProps & StateProps & DispatchProps

interface State {
    selectedDocuments: ISelectedDocument[]
}

export class CNIInlineAttachment extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            selectedDocuments: [],
        }
    }

    render(): JSX.Element {
        const { ipfName, customFieldDefinitionId, lightbox } = this.props

        return (
            <InlineAttachment
                wrapperClasses="tw-p-2 tw-w-full md:tw-w-1/2 lg:tw-w-1/3"
                canEditAttachments={true}
                canAddAttachments={true}
                canDownloadAttachments={false}
                fieldName={ipfName}
                customFieldDefinitionId={customFieldDefinitionId}
                attachments={this.state.selectedDocuments.map(
                    this.toAttachment,
                )}
                attachmentLoadCounter={-1}
                showAllDocuments={true}
                onDeleteAttachment={this.onDeleteAttachment}
                onAddAttachment={this.onAddAttachment}
                onUpdateAttachmentDescription={
                    this.onUpdateAttachmentDescription
                }
                showLightbox={this.showLightbox}
                hideLightbox={this.hideLightbox}
                changeAttachmentInLightbox={this.changeAttachmentInLightbox}
                lightbox={lightbox}
                maxInitialCards={9}
            />
        )
    }

    toAttachment = (document: ISelectedDocument): IAttachment => ({
        creator: this.props.fullName,
        fileProperties: document.fileSize,
        key: document.key,
        fileName: document.file.name,
        thumbnail: document.thumbnail,
        description: document.description,
    })

    keyCounter = 0

    onAddAttachment = async (request: UploadRequest): Promise<void> => {
        const addedAttachment: ISelectedDocument = {
            fileSize: (request.file.size / 1024).toFixed(2) + "KB",
            key: this.keyCounter++,
            thumbnail: isImage(request.file.name)
                ? await toBase64(request.file)
                : undefined,
            file: request.file,
            description: "",
        }

        this.setState(({ selectedDocuments }) => {
            const newSelectedDocuments = [addedAttachment, ...selectedDocuments]

            this.props.ipfValue.splice(
                0,
                this.props.ipfValue.length,
                ...newSelectedDocuments.map((a) => a.file),
            )

            return {
                selectedDocuments: newSelectedDocuments,
            }
        })
    }

    onDeleteAttachment = (key: number): void => {
        const { selectedDocuments } = this.state
        const {
            lightboxIndex,
            customFieldDefinitionId,
            updateLightboxVisibility,
            increaseLightboxCurrentIndex,
        } = this.props

        const shouldMoveToPreviousIndex =
            lightboxIndex === selectedDocuments.length - 1
        const newSelectedDocuments = selectedDocuments.filter(
            (a) => a.key !== key,
        )

        this.props.ipfValue.splice(
            0,
            this.props.ipfValue.length,
            ...newSelectedDocuments.map((a) => a.file),
        )

        this.setState({ selectedDocuments: newSelectedDocuments })

        if (newSelectedDocuments.length === 0) {
            updateLightboxVisibility(customFieldDefinitionId, false)
        } else if (shouldMoveToPreviousIndex) {
            increaseLightboxCurrentIndex(customFieldDefinitionId, -1)
        }
    }

    onUpdateAttachmentDescription = (key: number, value: string): void => {
        const newSelectedDocuments = this.state.selectedDocuments.map(
            (attachment) => {
                if (attachment.key === key) {
                    attachment.description = value
                }
                return attachment
            },
        )
        this.setState({ selectedDocuments: newSelectedDocuments })
    }

    showLightbox = (lightboxStartingIndex: number): void => {
        const { customFieldDefinitionId, updateLightboxVisibility } = this.props
        updateLightboxVisibility(
            customFieldDefinitionId,
            true,
            lightboxStartingIndex,
        )
    }

    hideLightbox = (): void => {
        const { customFieldDefinitionId, updateLightboxVisibility } = this.props
        updateLightboxVisibility(customFieldDefinitionId, false)
    }

    changeAttachmentInLightbox = (diff: number): void => {
        const { customFieldDefinitionId, increaseLightboxCurrentIndex } =
            this.props
        increaseLightboxCurrentIndex(customFieldDefinitionId, diff)
    }
}

const mapStateToProps = (state: GlobalState, props: OwnProps): StateProps => ({
    fullName: state.userInfo.fullName,
    lightboxIndex:
        state.inlineAttachments.lightboxes[props.customFieldDefinitionId]
            .currentIndex,
    lightbox: state.inlineAttachments.lightboxes[props.customFieldDefinitionId],
})

export default connect(mapStateToProps, mapDispatchToProps)(CNIInlineAttachment)
